import { z } from "zod";

// ─── Apparition — Foreground Transparent Character ────────────────────────
//
// A transparent character video (bridal silhouette, bird, ribbon, etc.) is
// `position: fixed` overlay across the page. Its translate/scale/rotate is
// driven by a scroll-progress timeline interpolating between keyframes.
//
// Browser support requires dual encoding:
// - WebM/VP9 alpha (Chrome, Firefox, Edge)
// - HEVC alpha (Safari)
// The HEVC <source> goes first so Safari picks it up. WebM is the fallback.

const hexColor = z.string().regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Geçersiz renk");

export const apparitionKeyframeSchema = z.object({
  /** Scroll progress 0..1 where this keyframe takes effect. */
  at: z.number().min(0).max(1),
  /** Horizontal position as a percent of viewport width. */
  x: z.number().min(-150).max(250),
  /** Vertical position as a percent of viewport height. */
  y: z.number().min(-50).max(150),
  /** Scale multiplier (1 = native). */
  scale: z.number().min(0.05).max(5),
  /** Rotation in degrees. */
  rotate: z.number().min(-360).max(360),
  /** Opacity 0..1. */
  opacity: z.number().min(0).max(1),
});

export const apparitionConfigSchema = z.object({
  hero: z.object({
    label: z.string().max(120),
    tagline: z.string().max(240),
  }),
  character: z.object({
    /** Optional WebM URL (VP9 alpha). Either this or hevcUrl should be set. */
    webmUrl: z.string().max(2048),
    /** Optional HEVC MP4 URL (alpha). Safari uses this. */
    hevcUrl: z.string().max(2048),
    /** 1–5 keyframes for the scroll path. */
    keyframes: z.array(apparitionKeyframeSchema).min(2).max(5),
  }),
  story: z.object({
    label: z.string().max(60),
    headline: z.string().max(160),
    body: z.string().max(800),
  }),
  date: z.object({
    label: z.string().max(60),
  }),
  venue: z.object({
    label: z.string().max(60),
  }),
  rsvp: z.object({
    label: z.string().max(60),
    heading: z.string().max(80),
    closingPrefix: z.string().max(160),
  }),
  colors: z.object({
    accent: hexColor,
    background: hexColor,
    text: hexColor,
  }),
});

export type ApparitionKeyframe = z.infer<typeof apparitionKeyframeSchema>;
export type ApparitionConfig = z.infer<typeof apparitionConfigSchema>;

export const APPARITION_DEFAULTS: ApparitionConfig = {
  hero: {
    label: "An Apparition",
    tagline: "Sevincimiz size doğru yürüyor.",
  },
  character: {
    webmUrl: "",
    hevcUrl: "",
    // Default path: enters from left, drifts across hero, fades, returns near RSVP.
    keyframes: [
      { at: 0.00, x: -20, y: 60, scale: 0.8, rotate: -3, opacity: 0 },
      { at: 0.15, x: 12,  y: 50, scale: 1.0, rotate: 0,  opacity: 1 },
      { at: 0.45, x: 55,  y: 35, scale: 1.1, rotate: 2,  opacity: 0.9 },
      { at: 0.75, x: 90,  y: 60, scale: 0.9, rotate: -2, opacity: 0.6 },
      { at: 1.00, x: 120, y: 70, scale: 0.7, rotate: 0,  opacity: 0 },
    ],
  },
  story: {
    label: "— Hikayemiz —",
    headline: "İki ruh,\nbir yolculuk.",
    body: "{description}",
  },
  date: {
    label: "● Tarih ●",
  },
  venue: {
    label: "● Mekan ●",
  },
  rsvp: {
    label: "● RSVP ●",
    heading: "Yanınızda olmanızı istiyoruz",
    closingPrefix: "Lütfen {rsvpBy} tarihine kadar onaylayın",
  },
  colors: {
    accent: "#c9a96e",
    background: "#0c0a08",
    text: "#f4ede4",
  },
};
